/**
 * ReportsView — the Reports top-level tab (nav slot 8).
 *
 * HubSpot/Databox-style reporting on top of the app's REAL data:
 *
 *   (a) A gallery of DEFAULT dashboards (Sales & Pipeline, Content,
 *       Customer Health, Agent & Autonomy) — each card computes live KPIs
 *       through the pure engine in src/lib/reports/reportMetrics.ts, which
 *       reuses the existing sources (health scoring, the agent value
 *       ledger, ticket/content pipelines). Nothing is fabricated: a metric
 *       with no underlying data says so instead of showing a fake zero.
 *
 *   (b) A REPORT TEMPLATES gallery (Sales, Pipeline, Customer Health,
 *       Content, Agent/Autonomy, Weekly Exec) — one click saves the whole
 *       bundle as real saved reports via the pure template→report builder
 *       in src/lib/reports/reportTemplates.ts. Deterministic per-name ids
 *       make re-applying a template an upsert, never a duplicate pile.
 *
 *   (c) A build-your-own report: pick a metric from the same registry,
 *       choose stat-card or bar-breakdown, name it, save it. Persistence
 *       is src/lib/db/savedReports.ts (mirrors tickets/savedViews), so
 *       custom cards survive reload and sync like everything else. Saved
 *       reports store WHAT to compute — the number is always recomputed
 *       live from the current collections.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Bot,
  Check,
  HeartPulse,
  LayoutTemplate,
  LineChart,
  PenSquare,
  Plus,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { LoadingState, EmptyState } from '../components/States';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  computeReportMetric,
  DEFAULT_DASHBOARDS,
  REPORT_METRICS,
  type ReportCategory,
  type ReportData,
  type ReportDisplay,
  type ReportMetricKind,
  type ReportMetricResult,
} from '../lib/reports/reportMetrics';
import { subscribePeople } from '../lib/db/people';
import { subscribeTickets } from '../lib/db/tickets';
import { subscribeContentItems } from '../lib/db/contentItems';
import { subscribeAgents } from '../lib/db/agents';
import { subscribeApprovals } from '../lib/agent/approvals';
import {
  REPORT_TEMPLATES,
  applyReportTemplate,
  type ReportTemplate,
} from '../lib/reports/reportTemplates';
import {
  saveReport,
  savedReportId,
  removeSavedReport,
  subscribeSavedReports,
} from '../lib/db/savedReports';
import type {
  ApprovalItem,
  ContentItem,
  DeployedAgent,
  Person,
  SavedReport,
  Ticket,
} from '../lib/db/types';

// --- Presentation maps -------------------------------------------------------

const CATEGORY_META: Record<
  ReportCategory,
  { icon: typeof TrendingUp; color: string; bg: string; eyebrow: string }
> = {
  sales: {
    icon: TrendingUp,
    color: C.blueLight,
    bg: 'linear-gradient(135deg, rgba(0,130,243,0.22), rgba(47,1,253,0.10))',
    eyebrow: 'Revenue',
  },
  content: {
    icon: PenSquare,
    color: C.orange,
    bg: 'linear-gradient(135deg, rgba(253,173,1,0.20), rgba(226,57,57,0.10))',
    eyebrow: 'Marketing',
  },
  health: {
    icon: HeartPulse,
    color: C.green,
    bg: 'linear-gradient(135deg, rgba(34,197,94,0.20), rgba(2,122,72,0.10))',
    eyebrow: 'Customer success',
  },
  agents: {
    icon: Bot,
    color: C.magenta,
    bg: 'linear-gradient(135deg, rgba(255,0,255,0.20), rgba(187,65,152,0.10))',
    eyebrow: 'Autonomy',
  },
};

const CATEGORY_LABELS: Record<ReportCategory, string> = {
  sales: 'Sales & Pipeline',
  content: 'Content',
  health: 'Customer Health',
  agents: 'Agents & Delivery',
};

// ===========================================================================

export function ReportsView() {
  const { hasRole, user } = useAuth();
  const canMutate = hasRole('staff');

  // Live collections — every card recomputes from these on each snapshot.
  const [people, setPeople] = useState<Person[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);
  const [agents, setAgents] = useState<DeployedAgent[]>([]);
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let seen = 0;
    // Count first snapshots so we show a skeleton, not a premature zero.
    const first = () => {
      seen += 1;
      if (seen >= 6) setLoaded(true);
    };
    const once = (fn: () => void) => {
      let done = false;
      return () => {
        if (!done) {
          done = true;
          fn();
        }
      };
    };
    const marks = Array.from({ length: 6 }, () => once(first));
    const unsubs = [
      subscribePeople((rows) => {
        setPeople(rows);
        marks[0]();
      }),
      subscribeTickets((rows) => {
        setTickets(rows);
        marks[1]();
      }),
      subscribeContentItems((rows) => {
        setContentItems(rows);
        marks[2]();
      }),
      subscribeApprovals((rows) => {
        setApprovals(rows);
        marks[3]();
      }),
      subscribeAgents((rows) => {
        setAgents(rows);
        marks[4]();
      }),
      subscribeSavedReports((rows) => {
        setSavedReports(rows);
        marks[5]();
      }),
    ];
    return () => unsubs.forEach((u) => u());
  }, []);

  const data: ReportData = useMemo(
    () => ({ people, tickets, contentItems, approvals, agents }),
    [people, tickets, contentItems, approvals, agents],
  );

  // --- Builder state ---------------------------------------------------------
  const [draftName, setDraftName] = useState('');
  const [draftMetric, setDraftMetric] = useState<ReportMetricKind>('pipeline-value');
  const [draftDisplay, setDraftDisplay] = useState<ReportDisplay>('stat');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [applyingTpl, setApplyingTpl] = useState<string | null>(null);
  const [tplNotice, setTplNotice] = useState<string | null>(null);
  const [tplError, setTplError] = useState<string | null>(null);

  const draftPreview = useMemo(
    () => computeReportMetric(draftMetric, data),
    [draftMetric, data],
  );
  const draftDef = REPORT_METRICS.find((m) => m.kind === draftMetric)!;

  // Which saved-report ids already exist — lets a template card show
  // "Added" honestly (ids are deterministic per name).
  const savedIds = useMemo(
    () => new Set(savedReports.map((r) => r.id)),
    [savedReports],
  );
  function templateApplied(tpl: ReportTemplate): boolean {
    return tpl.cards.every((c) => savedIds.has(savedReportId(c.name)));
  }

  async function onApplyTemplate(tpl: ReportTemplate) {
    setApplyingTpl(tpl.id);
    setTplError(null);
    setTplNotice(null);
    try {
      const saved = await applyReportTemplate(
        tpl,
        user?.displayName ? { createdBy: user.displayName } : {},
      );
      setTplNotice(
        `Added the “${tpl.title}” template — ${saved.length} report${saved.length === 1 ? '' : 's'} saved below.`,
      );
    } catch {
      setTplError(`Applying the “${tpl.title}” template failed — try again.`);
    } finally {
      setApplyingTpl(null);
    }
  }

  async function onSave() {
    // No name typed? Default to the metric's label — one less step.
    const name = draftName.trim() || draftDef.label;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await saveReport({
        name,
        metric: draftMetric,
        display: draftDisplay,
        ...(user?.displayName ? { createdBy: user.displayName } : {}),
      });
      setDraftName('');
      setNotice(`Saved “${name}” — it renders live under Saved reports below.`);
    } catch {
      setError('Saving the report failed — try again.');
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string) {
    setError(null);
    try {
      await removeSavedReport(id);
    } catch {
      setError('Deleting the report failed — try again.');
    }
  }

  return (
    <div className="app-container" style={{ display: 'grid', gap: 16, padding: '20px 0' }}>
      {/* Header */}
      <div>
        <span className="t-eyebrow">Reports</span>
        <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
          Reports
        </h1>
        <p className="t-meta" style={{ margin: '6px 0 0' }}>
          Live dashboards computed from your real CRM, content, health, and agent data —
          plus your own saved reports.
        </p>
      </div>

      {!loaded ? (
        <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
          <LoadingState rows={4} label="Loading report data" />
        </GlassPanel>
      ) : (
        <>
          {/* (a) Default dashboards gallery */}
          {DEFAULT_DASHBOARDS.map((dash) => {
            const meta = CATEGORY_META[dash.category];
            const Icon = meta.icon;
            return (
              <GlassPanel key={dash.id} variant="default" style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      display: 'inline-grid',
                      placeItems: 'center',
                      background: meta.bg,
                      border: '1px solid rgba(255,255,255,0.14)',
                      color: meta.color,
                    }}
                  >
                    <Icon size={17} strokeWidth={2} />
                  </span>
                  <div>
                    <span className="t-eyebrow">{meta.eyebrow}</span>
                    <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                      {dash.title}
                    </h2>
                  </div>
                </div>
                <p className="t-meta" style={{ margin: '0 0 12px', color: C.textMuted }}>
                  {dash.description}
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 10,
                  }}
                >
                  {dash.metrics.map((kind) => (
                    <MetricCard
                      key={kind}
                      result={computeReportMetric(kind, data)}
                      accent={meta.color}
                      showBars
                    />
                  ))}
                </div>
              </GlassPanel>
            );
          })}

          {/* (b) Report templates gallery — one click creates saved reports */}
          <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  display: 'inline-grid',
                  placeItems: 'center',
                  background: 'linear-gradient(135deg, rgba(253,173,1,0.20), rgba(0,130,243,0.12))',
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: C.orange,
                }}
              >
                <LayoutTemplate size={17} strokeWidth={2} />
              </span>
              <div>
                <span className="t-eyebrow">One-click setup</span>
                <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                  Report templates
                </h2>
              </div>
            </div>
            <p className="t-meta" style={{ margin: '0 0 12px', color: C.textMuted }}>
              {canMutate
                ? 'Pick a template to add its cards to your saved reports — every card computes live from your real data, and re-applying updates in place instead of duplicating.'
                : 'Read-only access — an editor can add these template bundles to the saved reports below.'}
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 10,
              }}
            >
              {REPORT_TEMPLATES.map((tpl) => {
                const meta = CATEGORY_META[tpl.category];
                const applied = templateApplied(tpl);
                const applying = applyingTpl === tpl.id;
                return (
                  <GlassPanel
                    key={tpl.id}
                    variant="subtle"
                    animate={false}
                    style={{
                      padding: '14px 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div>
                      <span className="t-eyebrow" style={{ color: meta.color }}>
                        {meta.eyebrow}
                      </span>
                      <span className="t-card-title" style={{ display: 'block', marginTop: 2 }}>
                        {tpl.title}
                      </span>
                    </div>
                    <p className="t-meta" style={{ margin: 0, color: C.textMuted }}>
                      {tpl.description}
                    </p>
                    <ul
                      className="t-meta"
                      style={{
                        margin: 0,
                        paddingLeft: 16,
                        color: C.textDim,
                        display: 'grid',
                        gap: 2,
                      }}
                    >
                      {tpl.cards.map((card) => (
                        <li key={card.name}>{card.name}</li>
                      ))}
                    </ul>
                    {canMutate && (
                      <button
                        type="button"
                        className={applied ? 'glass-btn' : 'glass-btn glass-btn--primary'}
                        onClick={() => void onApplyTemplate(tpl)}
                        disabled={applying}
                        style={{
                          marginTop: 'auto',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          alignSelf: 'flex-start',
                        }}
                      >
                        {applying ? (
                          'Adding…'
                        ) : applied ? (
                          <>
                            <Check size={13} /> Added — re-apply
                          </>
                        ) : (
                          <>
                            <Plus size={13} /> Use template ({tpl.cards.length}{' '}
                            {tpl.cards.length === 1 ? 'card' : 'cards'})
                          </>
                        )}
                      </button>
                    )}
                  </GlassPanel>
                );
              })}
            </div>
            {tplNotice && (
              <p className="t-meta" role="status" style={{ margin: '10px 0 0', color: C.green }}>
                {tplNotice}
              </p>
            )}
            {tplError && (
              <p className="t-meta" role="alert" style={{ margin: '10px 0 0', color: C.magenta }}>
                {tplError}
              </p>
            )}
          </GlassPanel>

          {/* (c) Build your own report */}
          <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  display: 'inline-grid',
                  placeItems: 'center',
                  background: 'linear-gradient(135deg, rgba(0,130,243,0.20), rgba(255,0,255,0.10))',
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: C.blueLight,
                }}
              >
                <LineChart size={17} strokeWidth={2} />
              </span>
              <div>
                <span className="t-eyebrow">Report builder</span>
                <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                  Build your own
                </h2>
              </div>
            </div>

            {canMutate ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                  alignItems: 'flex-end',
                  marginBottom: 14,
                }}
              >
                <label style={{ display: 'grid', gap: 4, flex: '1 1 220px' }}>
                  <span className="t-meta">1 · Pick a metric</span>
                  <select
                    className="glass-input"
                    value={draftMetric}
                    onChange={(e) => setDraftMetric(e.target.value as ReportMetricKind)}
                  >
                    {(Object.keys(CATEGORY_LABELS) as ReportCategory[]).map((cat) => (
                      <optgroup key={cat} label={CATEGORY_LABELS[cat]}>
                        {REPORT_METRICS.filter((m) => m.category === cat).map((m) => (
                          <option key={m.kind} value={m.kind}>
                            {m.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 4, flex: '0 1 160px' }}>
                  <span className="t-meta">2 · Card style</span>
                  <select
                    className="glass-input"
                    value={draftDisplay}
                    onChange={(e) => setDraftDisplay(e.target.value as ReportDisplay)}
                  >
                    <option value="stat">Stat card</option>
                    <option value="bars">Bar breakdown</option>
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 4, flex: '1 1 180px' }}>
                  <span className="t-meta">3 · Name it</span>
                  <input
                    className="glass-input"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    placeholder={draftDef.label}
                    title="Leave blank to use the metric's name"
                  />
                </label>
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={() => void onSave()}
                  disabled={saving}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <Plus size={13} /> {saving ? 'Saving…' : '4 · Save report'}
                </button>
              </div>
            ) : (
              <p className="t-meta" style={{ margin: '0 0 12px', color: C.textMuted }}>
                Read-only access — editors can build and save reports.
              </p>
            )}

            {canMutate && (
              <p className="t-meta" style={{ margin: '0 0 10px', color: C.textDim }}>
                {draftDef.description} Leave the name blank to call it “{draftDef.label}”.
              </p>
            )}

            {error && (
              <p className="t-meta" role="alert" style={{ margin: '0 0 10px', color: C.magenta }}>
                {error}
              </p>
            )}
            {notice && (
              <p className="t-meta" role="status" style={{ margin: '0 0 10px', color: C.green }}>
                {notice}
              </p>
            )}

            {/* Live preview of the metric being configured */}
            {canMutate && (
              <div style={{ marginBottom: 16, maxWidth: 320 }}>
                <span className="t-meta" style={{ display: 'block', marginBottom: 6, color: C.textDim }}>
                  Preview — computed live from your data
                </span>
                <MetricCard
                  result={draftPreview}
                  accent={CATEGORY_META[REPORT_METRICS.find((m) => m.kind === draftMetric)!.category].color}
                  showBars={draftDisplay === 'bars'}
                />
              </div>
            )}

            {/* Saved reports */}
            <h3 className="t-section-title" style={{ margin: '0 0 10px' }}>
              Saved reports
            </h3>
            {savedReports.length === 0 ? (
              <EmptyState
                icon={<BarChart3 size={22} strokeWidth={1.75} />}
                title="No saved reports yet."
                hint={
                  canMutate
                    ? 'Pick a metric and a card style above, name it, and save it.'
                    : 'An editor can build one from the metric registry.'
                }
              />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 10,
                }}
              >
                {savedReports.map((report) => {
                  const def = REPORT_METRICS.find((m) => m.kind === report.metric);
                  // A report saved against a metric this build no longer knows
                  // is shown honestly as unavailable rather than crashing.
                  if (!def) {
                    return (
                      <GlassPanel key={report.id} variant="subtle" animate={false} style={{ padding: '14px 16px' }}>
                        <span className="t-card-title">{report.name}</span>
                        <p className="t-meta" style={{ margin: '6px 0 0', color: C.textMuted }}>
                          Metric “{report.metric}” is not available in this build.
                        </p>
                      </GlassPanel>
                    );
                  }
                  return (
                    <MetricCard
                      key={report.id}
                      result={computeReportMetric(report.metric, data)}
                      accent={CATEGORY_META[def.category].color}
                      showBars={report.display === 'bars'}
                      title={report.name}
                      subtitle={def.label}
                      onDelete={canMutate ? () => void onDelete(report.id) : undefined}
                    />
                  );
                })}
              </div>
            )}
          </GlassPanel>
        </>
      )}
    </div>
  );
}

// --- One KPI card ------------------------------------------------------------

function MetricCard({
  result,
  accent,
  showBars,
  title,
  subtitle,
  onDelete,
}: {
  result: ReportMetricResult;
  accent: string;
  /** Render the breakdown bar list (when the metric has one). */
  showBars: boolean;
  /** Custom card name (saved reports); falls back to the metric label. */
  title?: string;
  /** Small line under a custom title naming the underlying metric. */
  subtitle?: string;
  onDelete?: () => void;
}) {
  const max = result.breakdown.reduce((m, s) => Math.max(m, s.value), 0);
  return (
    <GlassPanel
      variant="subtle"
      animate={false}
      style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <span className="t-card-title" style={{ display: 'block' }}>
            {title ?? result.label}
          </span>
          {subtitle && (
            <span className="t-meta" style={{ color: C.textDim }}>
              {subtitle}
            </span>
          )}
        </div>
        {onDelete && (
          <button
            type="button"
            className="glass-btn"
            aria-label={`Delete report ${title ?? result.label}`}
            title="Delete this saved report"
            onClick={onDelete}
            style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center' }}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>

      <span
        style={{
          fontSize: 28,
          fontWeight: 700,
          lineHeight: 1.1,
          color: result.empty ? C.textDim : accent,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {result.display}
      </span>

      {result.empty ? (
        <p className="t-meta" style={{ margin: 0, color: C.textDim }}>
          {result.emptyHint ?? 'No data yet.'}
        </p>
      ) : (
        showBars &&
        result.breakdown.length > 0 && (
          <div style={{ display: 'grid', gap: 5 }}>
            {result.breakdown.map((slice) => (
              <div key={slice.label} style={{ display: 'grid', gap: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span className="t-meta" style={{ color: C.textMuted, textTransform: 'capitalize' }}>
                    {slice.label}
                  </span>
                  <span className="t-meta" style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}>
                    {slice.value.toLocaleString('en-US')}
                  </span>
                </div>
                <div
                  aria-hidden="true"
                  style={{
                    height: 4,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.08)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${max > 0 ? Math.max(4, (slice.value / max) * 100) : 0}%`,
                      borderRadius: 2,
                      background: accent,
                      opacity: 0.75,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </GlassPanel>
  );
}
